<template>
  <div class="home-discovery">
    <section class="home-discovery__hero" aria-labelledby="home-hero-title">
      <p class="home-discovery__brand">Compopo</p>
      <h1 id="home-hero-title" class="home-discovery__hero-title">
        {{ $l('compareIndexTitle') }}
      </h1>
      <home-hot-deals
        :country="country"
        :currency="currency"
        :currency-sign="currencySign"
      />
    </section>

    <section class="home-discovery__popular" aria-labelledby="home-popular-title">
      <h2 id="home-popular-title" class="home-discovery__section-heading">
        {{ $l('homePopularSearches') }}
      </h2>
      <div class="home-discovery__pills">
        <button
          v-for="item in popularSearches"
          :key="item.query"
          type="button"
          class="home-discovery__pill tw-pressable"
          @click="$emit('popular-search', item.query)"
        >
          {{ popularLabel(item) }}
        </button>
      </div>
    </section>

    <section class="home-discovery__categories" aria-labelledby="home-categories-title">
      <h2 id="home-categories-title" class="home-discovery__section-heading">
        {{ $l('categories') }}
      </h2>

      <div class="category-hero-grid">
        <category-hero-card
          v-for="section in categoryHeroSections"
          :id="`home-${section.key}`"
          :key="section.key"
          :category-key="section.key"
          :title="sectionTitle(section.key)"
          :image-src="getCategoryHeroImage(section.key)"
          :to="`/compare#${section.key}`"
        />
      </div>
    </section>

    <div class="home-discovery__footer">
      <router-link to="/compare" class="home-discovery__all-link tw-pressable">
        {{ $l('homeViewAllComparisons') }}
        <span aria-hidden="true">→</span>
      </router-link>
    </div>
  </div>
</template>

<script>
import {
  getHomeCategorySections,
  HOME_POPULAR_SEARCHES,
} from '@/seo/comparePages';
import { getCategoryHeroImage } from '@/seo/categoryHeroImages';
import HomeHotDeals from './HomeHotDeals.vue';
import CategoryHeroCard from './CategoryHeroCard.vue';

const SECTION_LABEL_KEYS = {
  gaming: 'compareIndexTabGaming',
  phones: 'compareIndexTabPhones',
  electronics: 'compareIndexTabHome',
  audio: 'compareIndexTabAudio',
  computing: 'compareIndexTabComputing',
  fashion: 'compareIndexTabFashion',
  sports: 'compareIndexTabSports',
  beauty: 'compareIndexTabBeauty',
  baby: 'compareIndexTabBaby',
  pets: 'compareIndexTabPets',
  kitchen: 'compareIndexTabKitchen',
  tools: 'compareIndexTabTools',
  toys: 'compareIndexTabToys',
};

export default {
  name: 'HomeDiscovery',
  components: {
    HomeHotDeals,
    CategoryHeroCard,
  },
  props: {
    country: {
      type: String,
      default: 'IL',
    },
    currency: {
      type: String,
      default: 'NIS',
    },
    currencySign: {
      type: String,
      default: '₪',
    },
  },
  computed: {
    categoryHeroSections() {
      return getHomeCategorySections().filter(
        (section) => Boolean(getCategoryHeroImage(section.key)),
      );
    },
    popularSearches() {
      return HOME_POPULAR_SEARCHES;
    },
  },
  methods: {
    getCategoryHeroImage,
    sectionTitle(key) {
      const labelKey = SECTION_LABEL_KEYS[key];
      return labelKey ? this.$l(labelKey) : key;
    },
    popularLabel(item) {
      return this.locale === 'he' ? item.labelHe : item.labelEn;
    },
  },
};
</script>

<style scoped>
.home-discovery {
  padding-bottom: 2.5rem;
}

.home-discovery__hero {
  margin-bottom: 1.5rem;
  border-radius: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.9);
  background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 45%, #f0f9ff 100%);
  padding: 1.25rem 1rem 1rem;
  text-align: center;
  overflow: hidden;
}

[dir='rtl'] .home-discovery__hero,
[dir='rtl'] .home-discovery__hero-title,
[dir='rtl'] .home-discovery__section-heading {
  text-align: right;
}

[dir='ltr'] .home-discovery__hero-title,
[dir='ltr'] .home-discovery__section-heading {
  text-align: left;
}

[dir='rtl'] .home-discovery__pills {
  justify-content: flex-start;
}

.home-discovery__brand {
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #2563eb;
}

.home-discovery__hero-title {
  margin: 0;
  font-size: clamp(1.35rem, 3vw, 1.875rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.02em;
  color: #0f172a;
}

.home-discovery__section-heading {
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
}

.home-discovery__popular {
  margin-bottom: 2rem;
}

.home-discovery__categories {
  margin-bottom: 0.5rem;
}

.home-discovery__categories > .home-discovery__section-heading {
  margin-bottom: 1.25rem;
}

.category-hero-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr);
}

.home-discovery__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.home-discovery__pill {
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  background: #fff;
  padding: 0.5rem 0.875rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}

.home-discovery__pill:hover {
  border-color: #93c5fd;
  background: #eff6ff;
  color: #1d4ed8;
}

.home-discovery__footer {
  margin-top: 1.5rem;
  text-align: center;
}

.home-discovery__all-link {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: 9999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 0.75rem 1.25rem;
  font-size: 0.9375rem;
  font-weight: 600;
  color: #1d4ed8;
  text-decoration: none;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.home-discovery__all-link:hover {
  border-color: #93c5fd;
  box-shadow: 0 4px 16px rgba(37, 99, 235, 0.12);
}

@media (min-width: 640px) {
  .category-hero-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1.125rem;
  }
}

@media (min-width: 1024px) {
  .home-discovery__hero {
    padding: 1.5rem 1.5rem 1.25rem;
    text-align: start;
  }

  [dir='rtl'] .home-discovery__hero {
    text-align: right;
  }

  [dir='ltr'] .home-discovery__hero {
    text-align: left;
  }

  .category-hero-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.25rem;
  }
}
</style>
